import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import imageCompression from 'browser-image-compression';
import { getAllAvatarVariantPaths } from '../../utils/avatarUtils';
import { invalidateProfileViewCache } from '../../utils/profileViewCache';
import AvatarCropper from '../profile/AvatarCropper';
import Loading from '../layout/Loading';
import '../events/CreateEventForm.css';
import './CreateEventPopup.css';
import './EditProfilePopup.css';

function EditProfilePopup({ onClose }) {
  const { t } = useTranslation(['profile', 'shared']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  
  // Dane profilu
  const [profile, setProfile] = useState(null);
  const [user, setUser] = useState(null);
  
  // Formularz
  const [formData, setFormData] = useState({
    nickname: '',
    first_name: '',
    last_name: '',
    city: '',
    gender: '',
    birth_date: '',
    avatar_url: '',
    description: ''
  });
  
  // Avatar upload - używamy useRef aby zachować referencję do File object
  const [avatarPreview, setAvatarPreview] = useState(null);
  const avatarFileRef = useRef(null);
  const editProfileErrorRef = useRef(null);
  const [hasAvatarFile, setHasAvatarFile] = useState(false);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImageSrc, setCropperImageSrc] = useState(null);

  const normalizeGenderValue = (value) => {
    if (!value) return '';
    if (value === 'male') return 'M';
    if (value === 'female') return 'F';
    return value;
  };

  const loadUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      if (!authUser) {
        setError(t('profile.edit.errorNotLoggedIn'));
        return;
      }

      setUser(authUser);

      const { data: userProfile, error: profileError } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .single();

      if (profileError) {
        console.error('Error loading profile:', profileError);
        setError(t('profile.edit.errorLoad'));
      } else {
        setProfile(userProfile);
        setFormData({
          nickname: userProfile.nickname || '',
          first_name: userProfile.first_name || '',
          last_name: userProfile.last_name || '',
          city: userProfile.city || '',
          gender: normalizeGenderValue(userProfile.gender),
          birth_date: userProfile.birth_date ? userProfile.birth_date.split('T')[0] : '',
          avatar_url: userProfile.avatar_url || '',
          description: userProfile.description || ''
        });
        setAvatarPreview(userProfile.avatar_url || null);
      }
    } catch (err) {
      console.error('Error loading user profile:', err);
      setError(t('profile.edit.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadUserProfile();
  }, [loadUserProfile]);

  useEffect(() => {
    if (!error) return;
    editProfileErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [error]);

  // Cleanup dla blob URLs przy unmount
  useEffect(() => {
    return () => {
      if (avatarPreview && typeof avatarPreview === 'string' && avatarPreview.startsWith('blob:')) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Sprawdź rozmiar pliku (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('profile.edit.avatarErrorSize'));
      return;
    }

    // Sprawdź typ pliku
    if (!file.type.startsWith('image/')) {
      setError(t('profile.edit.avatarErrorInvalidType'));
      return;
    }

    // Reset input value aby można było wybrać ten sam plik ponownie
    e.target.value = '';

    // Utwórz URL do wyświetlenia w cropperze
    const imageUrl = URL.createObjectURL(file);
    setCropperImageSrc(imageUrl);
    setShowCropper(true);
    setError(null);
  };

  const handleCropComplete = (croppedFile) => {
    // Revoke poprzedni blob URL jeśli istnieje
    if (avatarPreview && typeof avatarPreview === 'string' && avatarPreview.startsWith('blob:')) {
      URL.revokeObjectURL(avatarPreview);
    }
    if (cropperImageSrc) {
      URL.revokeObjectURL(cropperImageSrc);
    }

    // Ustaw przycięty plik
    avatarFileRef.current = croppedFile;
    setHasAvatarFile(true);
    setRemoveAvatar(false);

    // Utwórz preview z przyciętego pliku
    const previewUrl = URL.createObjectURL(croppedFile);
    setAvatarPreview(previewUrl);

    // Zamknij cropper
    setShowCropper(false);
    setCropperImageSrc(null);
  };

  const deleteStoredAvatar = async (avatarUrl) => {
    if (!avatarUrl) return;

    try {
      const paths = getAllAvatarVariantPaths(avatarUrl);
      if (paths.length > 0) {
        await supabase.storage.from('avatars').remove(paths);
      }
    } catch (err) {
      console.warn('Could not delete avatar:', err);
    }
  };

  const handleRemoveAvatar = () => {
    if (avatarPreview && typeof avatarPreview === 'string' && avatarPreview.startsWith('blob:')) {
      URL.revokeObjectURL(avatarPreview);
    }

    avatarFileRef.current = null;
    setHasAvatarFile(false);
    setRemoveAvatar(true);
    setAvatarPreview(null);
    setFormData(prev => ({
      ...prev,
      avatar_url: ''
    }));
    setError(null);
  };

  const handleCropCancel = () => {
    // Revoke blob URL jeśli istnieje
    if (cropperImageSrc) {
      URL.revokeObjectURL(cropperImageSrc);
    }
    setShowCropper(false);
    setCropperImageSrc(null);
  };

  const uploadAvatar = async (userId, file) => {
    if (!file) return null;

    try {
      setUploading(true);
      
      // Sprawdź czy to prawdziwy File object
      if (!(file instanceof File) && !(file instanceof Blob)) {
        throw new Error('Invalid file object');
      }
      
      // Usuń stary avatar jeśli istnieje (wszystkie warianty: 100, 200)
      await deleteStoredAvatar(profile?.avatar_url);

      // Przygotuj podstawowe informacje o pliku
      const fileExt = file.name.split('.').pop().toLowerCase();
      const timestamp = Date.now();
      const contentType = file.type || `image/${fileExt}`;

      // 1. Resize do głównej wersji 200px (maksymalnie 200px, zachowując proporcje)
      const options200 = {
        maxSizeMB: 0.5, // Maksymalny rozmiar pliku po kompresji
        maxWidthOrHeight: 200, // Maksymalna szerokość/wysokość
        useWebWorker: true, // Użyj Web Worker dla lepszej wydajności
        fileType: contentType
      };
      
      let compressed200;
      try {
        compressed200 = await imageCompression(file, options200);
        console.log('Original file size:', (file.size / 1024 / 1024).toFixed(2), 'MB');
        console.log('Compressed 200px size:', (compressed200.size / 1024 / 1024).toFixed(2), 'MB');
      } catch (compressionError) {
        console.error('Error compressing image:', compressionError);
        throw new Error('Failed to process image. Please try a different image.');
      }

      // 2. Resize do wersji 100px
      const options100 = {
        maxSizeMB: 0.2,
        maxWidthOrHeight: 100,
        useWebWorker: true,
        fileType: contentType
      };
      const compressed100 = await imageCompression(file, options100);

      // Przygotuj ścieżki dla wszystkich wariantów
      const filePath200 = `${userId}/${timestamp}_200.${fileExt}`;
      const filePath100 = `${userId}/${timestamp}_100.${fileExt}`;

      // Konwertuj wszystkie warianty na ArrayBuffer
      const arrayBuffer200 = await compressed200.arrayBuffer();
      const arrayBuffer100 = await compressed100.arrayBuffer();

      // Upload wszystkich wariantów równolegle
      const uploadPromises = [
        supabase.storage.from('avatars').upload(filePath200, arrayBuffer200, {
          cacheControl: '3600',
          upsert: false,
          contentType: contentType,
        }),
        supabase.storage.from('avatars').upload(filePath100, arrayBuffer100, {
          cacheControl: '3600',
          upsert: false,
          contentType: contentType,
        })
      ];

      const uploadResults = await Promise.all(uploadPromises);

      // Sprawdź czy wszystkie uploady się powiodły
      for (const result of uploadResults) {
        if (result.error) {
          console.error('Upload error details:', result.error);
          
          // Bardziej szczegółowe komunikaty błędów
          if (result.error.message?.includes('mime type') || result.error.message?.includes('not supported')) {
            throw new Error(`Invalid file type. Please use JPG, PNG, GIF, or WebP format.`);
          } else if (result.error.message?.includes('Bucket not found')) {
            throw new Error('Avatar storage bucket not found. Please contact administrator.');
          } else if (result.error.message?.includes('new row violates row-level security')) {
            throw new Error('Permission denied. You may not have permission to upload avatars.');
          }
          
          throw result.error;
        }
      }

      // Pobierz publiczny URL dla głównej wersji (200px) - to będzie zapisane w bazie
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath200);

      console.log('Avatar uploaded successfully with variants:', {
        '100px': filePath100,
        '200px': filePath200
      });

      return publicUrl;
    } catch (err) {
      console.error('Error uploading avatar:', err);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      let finalAvatarUrl = formData.avatar_url;

      // Upload avatara jeśli został wybrany nowy
      if (hasAvatarFile && avatarFileRef.current) {
        try {
          // Przekazujemy plik z ref, żeby upewnić się że używamy oryginalnego File object
          finalAvatarUrl = await uploadAvatar(user.id, avatarFileRef.current);
        } catch (uploadErr) {
          console.error('Avatar upload failed:', uploadErr);
          setError(t('profile.edit.avatarErrorUpload'));
          setSaving(false);
          return;
        }
      } else if (removeAvatar) {
        await deleteStoredAvatar(profile?.avatar_url);
        finalAvatarUrl = null;
      }

      const trimmedNickname = formData.nickname.trim();
      const trimmedFirst = formData.first_name.trim();
      const trimmedLast = formData.last_name.trim();

      /* W bazie zostaje też kolumna `name` (legacy) — bez synchronizacji widać „stare” imię mimo pustych pól. */
      const nameForLegacy =
        trimmedNickname ||
        (trimmedFirst && trimmedLast
          ? `${trimmedFirst} ${trimmedLast}`
          : trimmedFirst || trimmedLast);

      if (!trimmedNickname && !trimmedFirst && !trimmedLast) {
        setError(t('profile.edit.errorDisplayNameRequired'));
        setSaving(false);
        return;
      }

      if (trimmedNickname && (trimmedNickname.length < 2 || trimmedNickname.length > 30)) {
        setError(t('profile.edit.errorNicknameLength'));
        setSaving(false);
        return;
      }

      if (!nameForLegacy || nameForLegacy.length < 2) {
        setError(t('profile.edit.errorDisplayNameTooShort'));
        setSaving(false);
        return;
      }

      if (nameForLegacy.length > 40) {
        setError(t('profile.edit.errorDisplayNameTooLong'));
        setSaving(false);
        return;
      }

      // Sprawdź czy nickname już istnieje (jeśli został zmieniony)
      if (trimmedNickname && trimmedNickname !== profile?.nickname) {
        const { data: existingUser } = await supabase
          .from('users')
          .select('id')
          .eq('nickname', trimmedNickname)
          .neq('id', user.id)
          .single();

        if (existingUser) {
          setError(t('profile.edit.errorNicknameTaken'));
          setSaving(false);
          return;
        }
      }

      // Przygotuj dane do aktualizacji
      const updateData = {
        nickname: trimmedNickname || null,
        first_name: trimmedFirst || null,
        last_name: trimmedLast || null,
        name: nameForLegacy,
        city: formData.city.trim() || null,
        gender: normalizeGenderValue(formData.gender) || null,
        birth_date: formData.birth_date || null,
        avatar_url: finalAvatarUrl || null,
        description: formData.description.trim() || null
      };

      // Aktualizuj profil
      const { error: updateError } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', user.id);

      if (updateError) {
        // Sprawdź czy to błąd unikalności nicku
        if (updateError.code === '23505' && updateError.message.includes('nickname')) {
          setError(t('profile.edit.errorNicknameTaken'));
        } else {
          throw updateError;
        }
        setSaving(false);
        return;
      }

      invalidateProfileViewCache(user.id);
      window.dispatchEvent(new CustomEvent('profileUpdated'));

      // Zamknij popup
      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err.message || t('profile.edit.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  if (loading) {
    return (
      <div className="create-event-popup create-event-popup--shell">
        <div className="edit-profile-loading-inner">
          <Loading className="edit-profile-loading" size="medium" />
        </div>
      </div>
    );
  }

  return (
    <>
      {showCropper && cropperImageSrc && (
        <AvatarCropper
          imageSrc={cropperImageSrc}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
      <div className="create-event-popup create-event-popup--shell">
        <form
          onSubmit={handleSubmit}
          className="create-event-form create-event-form--compact edit-profile-form"
        >
          <div className="form-group edit-profile-avatar-panel">
            <div className="edit-profile-avatar-layout">
              <div className="edit-profile-avatar-visual">
                <div className="edit-profile-avatar-container">
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt={t('profile.info.avatarAlt')}
                      className="edit-profile-avatar-preview"
                    />
                  ) : (
                    <div className="edit-profile-avatar-placeholder">
                      <span>👤</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="edit-profile-avatar-meta">
                <div className="edit-profile-avatar-heading">{t('profile.edit.avatar')}</div>
                <p className="edit-profile-avatar-hint">{t('profile.edit.avatarHint')}</p>
                <input
                  type="file"
                  id="avatar-upload"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleAvatarChange}
                  className="edit-profile-avatar-input"
                  disabled={uploading || saving}
                />
                <div className="edit-profile-avatar-actions">
                  <label htmlFor="avatar-upload" className="btn-secondary btn-small edit-profile-avatar-upload-label">
                    {uploading
                      ? t('profile.edit.avatarUploading')
                      : avatarPreview
                        ? t('profile.edit.avatarChange')
                        : t('profile.edit.avatarUpload')}
                  </label>
                  {(avatarPreview || formData.avatar_url) && (
                    <button
                      type="button"
                      className="btn-secondary btn-small edit-profile-avatar-remove-button"
                      onClick={handleRemoveAvatar}
                      disabled={uploading || saving}
                    >
                      {t('profile.edit.avatarRemove', 'Usuń zdjęcie')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">
              {t('profile.info.description')}
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={t('profile.edit.descriptionPlaceholder')}
              maxLength={500}
              rows={3}
              disabled={saving || uploading}
            />
            <div className="char-counter">
              {formData.description.length}/500
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="nickname">{t('profile.info.nickname')}</label>
            <input
              type="text"
              id="nickname"
              name="nickname"
              value={formData.nickname}
              onChange={handleInputChange}
              placeholder={t('profile.edit.nicknamePlaceholder')}
              disabled={saving || uploading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="first_name">{t('profile.info.firstName')}</label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              placeholder={t('profile.edit.firstNamePlaceholder')}
              disabled={saving || uploading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="last_name">{t('profile.info.lastName')}</label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              placeholder={t('profile.edit.lastNamePlaceholder')}
              disabled={saving || uploading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="city">{t('profile.info.city')}</label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              placeholder={t('profile.edit.cityPlaceholder')}
              disabled={saving || uploading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="gender">{t('profile.info.gender')}</label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleInputChange}
              disabled={saving || uploading}
            >
              <option value="">{t('profile.edit.genderSelect')}</option>
              <option value="M">{t('profile.info.genderOptions.M')}</option>
              <option value="F">{t('profile.info.genderOptions.F')}</option>
              <option value="other">{t('profile.info.genderOptions.other')}</option>
              <option value="prefer_not_to_say">{t('profile.info.genderOptions.prefer_not_to_say')}</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="birth_date">{t('profile.info.birthDate')}</label>
            <input
              type="date"
              id="birth_date"
              name="birth_date"
              value={formData.birth_date}
              onChange={handleInputChange}
              max={new Date().toISOString().split('T')[0]}
              disabled={saving || uploading}
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={handleCancel} disabled={saving || uploading}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-primary" disabled={saving || uploading}>
              {saving ? t('common.loading') : t('common.save')}
            </button>
          </div>

          {error && (
            <div ref={editProfileErrorRef} className="form-error" role="alert">
              {error}
            </div>
          )}
        </form>
      </div>
    </>
  );
}

export default EditProfilePopup;
